<i18n
  src="@/locales/views/packing_house/production/palletized/edit_package_group.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/package_group.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/package.yml" lang="yaml"></i18n>
<template>
  <div>
    <go-back-link
      :text="$t('labels.go_back_link')"
      :name="routeName('show_package_group')"
      :params="{
        packing_house_id: packing_house_id,
        packing_house_process_id: packing_house_process_id,
        id: id,
      }"
    >
    </go-back-link>

    <bread-crumbs
      :labels="[
        module_name,
        packing_house_process?.name,
        package_group?.pg_code,
      ]"
    />

    <alert-danger :errors_data="std_err" />

    <div v-if="load_general_data" class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>
        <div class="panel-body">
          <div class="space-y-8 divide-y divide-gray-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.package_group.pg_code") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model="package_group.pg_code"
                    :class="['input-text', isInvalid('pg_code')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.package_group.grouped_at") }}
                </label>
                <div class="mt-1">
                  <date-form v-model="package_group.grouped_at" />
                </div>
              </div>
              <div
                v-if="agro_exporter_processes.length > 1"
                class="sm:col-span-3"
              >
                <label class="label-form">
                  {{ $t("model.package_group.agro_exporter_process") }}
                </label>
                <div class="mt-1">
                  <select-form
                    class="flex-auto"
                    :options="agro_exporter_processes"
                    :clearable="false"
                    v-model="package_group.agro_exporter_process_id"
                  ></select-form>
                </div>
              </div>
            </div>
            <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-6">
                <label class="label-form">
                  {{ $t("model.package_group.comment") }}
                </label>
                <div class="mt-1">
                  <textarea
                    v-model="package_group.comment"
                    rows="3"
                    class="input-text-area"
                  />
                </div>
              </div>
            </div>
            <div v-for="(package_item, idx) in packages" :key="idx">
              <div v-show="!package_item._destroy && !package_item._remove">
                <div
                  class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8"
                  v-if="package_item.from_stock"
                >
                  <div class="sm:col-span-8">
                    <div class="text-right">
                      <button
                        @click.prevent="removePackage(package_item)"
                        class="text-red-500"
                      >
                        <i class="fa-solid fa-close fa-lg px-1"></i>
                      </button>
                    </div>
                  </div>
                  <package-from-stock
                    :unassigned_packages="unassigned_packages"
                    :total="total_unassigned_packages"
                    :weeks="weeks_unassigned_packages"
                    :default_week="package_default_week"
                    :unassigned_packages_by_weeks="unassigned_packages_by_weeks"
                    :new_assigned_package_ids="new_assigned_package_ids"
                    :packing_house_process_id="packing_house_process_id"
                    :fetchPackages="fetchUnassignedPackages"
                    @changeToNewPackage="changeToNewPackage(package_item)"
                    @udpateUnassignedPackagesByWeek="
                      (week, packages) =>
                        udpateUnassignedPackagesByWeek(week, packages)
                    "
                    v-model="package_item.id"
                    v-model:quantity="package_item.quantity"
                    v-model:week="package_item.week"
                    :default="JSON.parse(JSON.stringify(package_item))"
                  ></package-from-stock>
                  <!-- para el default se usa un deep clone de package_item para que este no cambia al actualizar los valores del package_item -->
                </div>
              </div>
            </div>
            <div class="col-span-6 sm:col-span-3 text-right py-3">
              <button
                v-if="can_assign_other_package"
                class="border rounded-md px-4 py-1"
                @click.prevent="addFromStockPackage()"
              >
                {{ $t("labels.add_assigned_package_link") }}
              </button>
              <span class="space-actions"></span>
              <p class="px-8 py-2 font-bold">
                {{ $t("model.package_group.total_packages") }}:
                {{ total_packages }}
              </p>
            </div>
          </div>
        </div>
        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button
              @click.prevent="gotToShowPackageGroup()"
              :label="$t('labels.cancel_button')"
            />
            <span class="space-actions"></span>
            <success-button
              v-if="!proc_save_button"
              @click.prevent="submit()"
              :label="$t('labels.save_link')"
            />
            <success-button-processing v-else :label="$t('labels.save_link')" />
          </div>
        </div>
      </div>
    </div>
    <loading-message v-else />
  </div>
</template>

<script>
import moment from "moment";

import Requester from "@/views/packing_house/requesters/production/palletized/edit_package_group.js";
import PackageFromStock from "@/components/views/packing_house/production/palletized/PackageFromStock.vue";

export default {
  name: "EditPackageGroup",
  props: {
    packing_house_id: { required: true },
    packing_house_process_id: { required: true },
    id: { required: true },
  },
  components: {
    PackageFromStock,
  },
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      packing_house_process: null,
      pg_code: null,
      package_group: null,
      agro_exporter_processes: [],
      unassigned_packages: [],
      packages: [],
      total_unassigned_packages: 0, // total de paquetes por asignar según el servidor, no referencia el total de la variable data en vue, sino en rails.
      weeks_unassigned_packages: [],
      unassigned_packages_by_weeks: {},
    };
  },
  computed: {
    can_send: function () {
      return this.valid_data && !this.proc_save_button;
    },
    visible_packages: function () {
      return this.packages.filter((package_item) => !package_item._destroy);
    },
    valid_data: function () {
      return Boolean(this.package_group.pg_code) && this.valid_packages;
    },
    valid_packages: function () {
      return !this.packages
        .map((package_item) => this.validPackage(package_item))
        .includes(false);
    },
    grouped_at_formatted: function () {
      return moment(this.package_group.grouped_at).format("YYYY-MM-DD");
    },
    last_visible_from_stock_package: function () {
      let functional_packages = this.packages.filter(
        (pkg) => pkg.from_stock && !pkg._destroy && !pkg._remove
      );
      if (functional_packages.length > 0)
        return functional_packages[functional_packages.length - 1];
      return null;
    },
    new_assigned_packages: function () {
      return this.packages.filter(
        (package_item) =>
          package_item.from_stock && package_item.id && !package_item._destroy
      );
    },
    new_assigned_package_ids: function () {
      return this.new_assigned_packages.map((package_item) => package_item.id);
    },
    package_default_week: function () {
      if (this.last_visible_from_stock_package)
        return this.last_visible_from_stock_package.week;
      if (this.package_group.grouped_at) {
        return moment(this.package_group.grouped_at).week();
      }
      return null;
    },
    empty_to_assign_slots: function () {
      return this.packages.filter(
        (package_item) => package_item.from_stock && package_item.id == null
      ).length;
    },
    can_assign_other_package: function () {
      return (
        this.total_unassigned_packages -
          this.new_assigned_package_ids.length -
          this.empty_to_assign_slots >
        0
      );
    },
    total_packages: function () {
      const packages = this.packages.filter(
        (p) => !p._destroy && !p._remove && p.quantity
      );
      let quantity = packages
        .map((p) => p.quantity)
        .reduce((accumulator, currentValue) => {
          return accumulator + currentValue;
        }, 0);
      return quantity;
    },
  },
  methods: {
    loadData: async function () {
      await this.fetchPackingHouseProcess();

      await this.fetchUnassignedPackages();
      this.load_general_data = true;
    },
    fetchUnassignedPackages: async function (filters = {}) {
      let string_query = "";
      Object.keys(filters).forEach((filter) => {
        string_query += `${filter}=${filters[filter]}`;
      });
      let { data } = await Requester.fetchUnassignedPackages(
        this.packing_house_id,
        this.packing_house_process_id,
        this.id,
        string_query
      );

      data.packages.forEach((pkg) => {
        pkg.full_label = `${pkg.full_label} - ${pkg.exporter_process_crop_category.name} - ${pkg.exported_batch.batch_code}`;
      })

      if (string_query) {
        this.unassigned_packages = data.packages;
      }
      this.total_unassigned_packages = data.total;
      this.weeks_unassigned_packages = data.weeks;
      return { ...data };
    },
    gotToShowPackageGroup: function () {
      this.$router.push({
        name: this.routeName("show_package_group"),
        params: {
          packing_house_id: this.packing_house_id,
          packing_house_process_id: this.packing_house_process_id,
        },
      });
    },

    addFromStockPackage: function () {
      let new_package = {
        id: null,
        from_stock: true,
        week: this.package_default_week,
      };
      this.packages.push(new_package);
    },

    udpateUnassignedPackagesByWeek: function (week, packages) {
      if (this.unassigned_packages_by_weeks[week]) return;

      this.unassigned_packages_by_weeks[week] = packages;
    },

    fetchPackingHouseProcess: async function () {
      let response = await Requester.fetchPackingHouseProcess(
        this.packing_house_id,
        this.packing_house_process_id
      );
      this.packing_house_process = response.data;
    },

    fetchAgroExporterProcesses: async function () {
      let response = await Requester.fetchAgroExporterProcesses(
        this.packing_house_id,
        this.packing_house_process_id
      );
      this.agro_exporter_processes = response.data;
    },

    fetchPackageGroup: async function () {
      let response = await Requester.fetchPackageGroup(
        this.packing_house_id,
        this.packing_house_process_id,
        this.id
      );
      this.pg_code = response.data.pg_code;
      this.package_group = response.data;
      this.fillInitalData(response.data);
    },

    fillInitalData: function (package_group) {
      this.packages = package_group.packages.map((package_item) => {
        return {
          ...package_item,
        };
      });
    },

    validPackage: function (package_item) {
      console.log(package_item);
      if (package_item._remove) return false;

      if (package_item.from_stock) return Boolean(package_item.id);
    },

    submit: async function () {
      if (!this.can_send) return;
      this.proc_save_button = true;
      try {
        let response = await Requester.update(
          this.packing_house_id,
          this.packing_house_process_id,
          this.id,
          {
            ...this.package_group,
            packages: this.packages,
          }
        );
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.gotToShowPackageGroup();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      }
      this.proc_save_button = false;
    },
    removePackage: function (package_item) {
      if (package_item.id && !package_item.from_form) {
        package_item._destroy = true;
      } else package_item._remove = true;
    },
  },
  created: function () {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          packing_house_id: this.packing_house_id,
        },
      }).then(() => {
        this.fetchPackageGroup().then(() => this.loadData());
      })
    );
  },
};
</script>
